/**
 * NPS National Register of Historic Places unrestricted public screen.
 *
 * Precise coordinates are used transiently for distance calculations and are
 * never returned, persisted, logged, or sent to report prompts. This dataset
 * cannot establish archaeological clearance or Section 106 compliance.
 */
import type { DataProvider, ProviderGeoContext, ProviderResult } from './types';
import { gridCellKey, CACHE_TTL, fetchWithTimeout, validateCoords } from './types';
import {
  arcGisRingToPoints,
  haversineMiles,
  pointInPolygon,
  pointToPolygonDistanceMiles,
  polygonDistanceMiles,
  searchEnvelope,
  type GeoPoint,
} from './siteDiligenceGeometry';
import { refdataBbox, tryRefdata } from '../refdata/refdataClient';
import type { RefdataFeature } from '../../types/refdata';

export const NPS_NRHP_SERVICE_URL =
  'https://mapservices.nps.gov/arcgis/rest/services/cultural_resources/nrhp_locations/MapServer';
export const NPS_NRHP_RECORD_LIMIT = 2_000;
const DETAILS_LIMIT = 25;

export interface NrhpListedPropertySummary {
  featureId: string;
  name: string;
  resourceType: string | null;
  listingStatus: string | null;
  certificationDate: string | null;
  distanceMi: number;
  onParcel: boolean | null;
}

export interface CulturalResourcesScreenData {
  listedPropertiesOnParcel: number | null;
  listedPropertiesWithin1Mi: number;
  listedPropertiesWithin5Mi: number;
  nearestDistanceMi: number | null;
  listedProperties: NrhpListedPropertySummary[];
  queriedFeatureCount: number;
  truncated: boolean;
  sourceVintage: string;
  screeningOnly: true;
  preciseLocationsWithheld: true;
}

interface ArcFeature {
  attributes?: Record<string, unknown>;
  geometry?: { x?: number; y?: number; rings?: number[][][] };
}

interface ArcResponse {
  features?: ArcFeature[];
  exceededTransferLimit?: boolean;
  error?: { message?: string };
}

type Fetcher = (url: string, timeoutMs?: number) => Promise<Response>;

function clean(value: unknown, max = 300): string | null {
  if (typeof value !== 'string') return null;
  const result = value.replace(/[\u0000-\u001f]+/g, ' ').trim();
  return result ? result.slice(0, max) : null;
}

function queryUrl(layer: 0 | 1, centre: GeoPoint, boundary: readonly GeoPoint[] | undefined): string {
  const box = searchEnvelope(centre, boundary, 5);
  const params = new URLSearchParams({
    where: "STATUS IS NULL OR UPPER(STATUS) NOT LIKE '%REMOVED%'",
    geometry: `${box.minLng},${box.minLat},${box.maxLng},${box.maxLat}`,
    geometryType: 'esriGeometryEnvelope',
    inSR: '4326',
    outSR: '4326',
    spatialRel: 'esriSpatialRelIntersects',
    outFields: 'OBJECTID,NRIS_Refnum,PROPERTY_ID,RESNAME,ResType,STATUS,CertDate,EDIT_DATE',
    returnGeometry: 'true',
    resultRecordCount: String(NPS_NRHP_RECORD_LIMIT),
    f: 'json',
  });
  return `${NPS_NRHP_SERVICE_URL}/${layer}/query?${params.toString()}`;
}

interface WorkingProperty extends NrhpListedPropertySummary {
  sourceLayer: 0 | 1;
}

function pointFeatureDistance(
  feature: ArcFeature,
  centre: GeoPoint,
  boundary: readonly GeoPoint[] | undefined,
): { distanceMi: number; onParcel: boolean | null } | null {
  const x = feature.geometry?.x;
  const y = feature.geometry?.y;
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  const point = { lng: x as number, lat: y as number };
  return {
    distanceMi: boundary ? pointToPolygonDistanceMiles(point, boundary) : haversineMiles(centre, point),
    onParcel: boundary ? pointInPolygon(point, boundary) : null,
  };
}

function polygonFeatureDistance(
  feature: ArcFeature,
  centre: GeoPoint,
  boundary: readonly GeoPoint[] | undefined,
): { distanceMi: number; onParcel: boolean | null } | null {
  const rings = (feature.geometry?.rings ?? []).map(arcGisRingToPoints).filter(ring => ring.length >= 3);
  if (rings.length === 0) return null;
  if (boundary) {
    const distanceMi = Math.min(...rings.map(ring => polygonDistanceMiles(boundary, ring)));
    return { distanceMi, onParcel: distanceMi === 0 };
  }
  const distanceMi = Math.min(...rings.map(ring => pointToPolygonDistanceMiles(centre, ring)));
  return { distanceMi, onParcel: null };
}

/** Map a refdata NRHP feature into the ArcFeature shape the merge logic
 *  consumes (Point → {x,y}; Polygon/MultiPolygon → {rings}). */
function refdataToArcFeature(feature: RefdataFeature): { arc: ArcFeature; layer: 0 | 1 } | null {
  const attributes: Record<string, unknown> = { ...feature.props };
  if (feature.geom.type === 'Point') {
    const [x, y] = feature.geom.coordinates;
    return { arc: { attributes, geometry: { x, y } }, layer: 0 };
  }
  if (feature.geom.type === 'Polygon') {
    return { arc: { attributes, geometry: { rings: feature.geom.coordinates as number[][][] } }, layer: 1 };
  }
  if (feature.geom.type === 'MultiPolygon') {
    return { arc: { attributes, geometry: { rings: feature.geom.coordinates.flat() as number[][][] } }, layer: 1 };
  }
  return null;
}

/** The live WHERE clause, applied in JS to snapshot rows: keep listings
 *  whose STATUS is null / not "…REMOVED…". */
function statusListed(props: RefdataFeature['props']): boolean {
  const status = props.STATUS;
  return status == null || (typeof status === 'string' && !status.toUpperCase().includes('REMOVED'));
}

export async function fetchNpsNationalRegisterScreen(
  lat: number,
  lng: number,
  geo?: ProviderGeoContext,
  fetcher: Fetcher = fetchWithTimeout,
): Promise<ProviderResult<CulturalResourcesScreenData>> {
  validateCoords(lat, lng);
  const centre = geo?.centroid ?? { lat, lng };
  const boundary = geo?.boundary && geo.boundary.length >= 3 ? geo.boundary : undefined;

  // RD-1 (C3) — local-first: one bbox query over the national snapshot
  // replaces both live layer queries. The privacy contract is unchanged:
  // snapshot coordinates are used transiently for distances and never
  // emitted (the merge logic below never copies geometry into results).
  let points: ArcResponse;
  let polygons: ArcResponse;
  let snapshotVintage: string | null = null;
  const box = searchEnvelope(centre, boundary, 5);
  const local = await tryRefdata('nps-nrhp', () =>
    refdataBbox('nps-nrhp', {
      minLat: box.minLat, minLng: box.minLng, maxLat: box.maxLat, maxLng: box.maxLng,
    }, NPS_NRHP_RECORD_LIMIT));
  if (local) {
    snapshotVintage = local.vintageDate;
    const pointFeatures: ArcFeature[] = [];
    const polygonFeatures: ArcFeature[] = [];
    for (const feature of local.features) {
      if (!statusListed(feature.props)) continue;
      const mapped = refdataToArcFeature(feature);
      if (!mapped) continue;
      (mapped.layer === 0 ? pointFeatures : polygonFeatures).push(mapped.arc);
    }
    points = { features: pointFeatures, exceededTransferLimit: local.truncated };
    polygons = { features: polygonFeatures, exceededTransferLimit: false };
  } else {
    const [pointResponse, polygonResponse] = await Promise.all([
      fetcher(queryUrl(0, centre, boundary), 30_000),
      fetcher(queryUrl(1, centre, boundary), 30_000),
    ]);
    if (!pointResponse.ok) throw new Error(`NPS NRHP point layer HTTP ${pointResponse.status}`);
    if (!polygonResponse.ok) throw new Error(`NPS NRHP polygon layer HTTP ${polygonResponse.status}`);
    [points, polygons] = await Promise.all([
      pointResponse.json() as Promise<ArcResponse>,
      polygonResponse.json() as Promise<ArcResponse>,
    ]);
    if (points.error) throw new Error(`NPS NRHP point layer error: ${points.error.message ?? 'unknown error'}`);
    if (polygons.error) throw new Error(`NPS NRHP polygon layer error: ${polygons.error.message ?? 'unknown error'}`);
  }

  const merged = new Map<string, WorkingProperty>();
  const consume = (feature: ArcFeature, layer: 0 | 1) => {
    const attrs = feature.attributes ?? {};
    const metric = layer === 0
      ? pointFeatureDistance(feature, centre, boundary)
      : polygonFeatureDistance(feature, centre, boundary);
    if (!metric || !Number.isFinite(metric.distanceMi) || metric.distanceMi > 5.000_001) return;
    const oid = clean(attrs.OBJECTID, 80) ?? String(merged.size + 1);
    const featureId = clean(attrs.NRIS_Refnum, 80) ?? clean(attrs.PROPERTY_ID, 80) ?? `${layer}-${oid}`;
    const candidate: WorkingProperty = {
      featureId,
      name: clean(attrs.RESNAME, 200) ?? 'Unnamed National Register property',
      resourceType: clean(attrs.ResType, 80),
      listingStatus: clean(attrs.STATUS, 80),
      certificationDate: clean(attrs.CertDate, 40),
      distanceMi: metric.distanceMi,
      onParcel: metric.onParcel,
      sourceLayer: layer,
    };
    const prior = merged.get(featureId);
    if (!prior || candidate.distanceMi < prior.distanceMi || candidate.onParcel === true) {
      merged.set(featureId, candidate);
    }
  };
  for (const feature of points.features ?? []) consume(feature, 0);
  for (const feature of polygons.features ?? []) consume(feature, 1);

  const all = [...merged.values()]
    .sort((a, b) => a.distanceMi - b.distanceMi || a.featureId.localeCompare(b.featureId));
  const truncated = points.exceededTransferLimit === true || polygons.exceededTransferLimit === true || all.length > DETAILS_LIMIT;
  const listedProperties = all.slice(0, DETAILS_LIMIT).map(({ sourceLayer: _sourceLayer, ...row }) => row);
  const data: CulturalResourcesScreenData = {
    listedPropertiesOnParcel: boundary ? all.filter(row => row.onParcel === true).length : null,
    listedPropertiesWithin1Mi: all.filter(row => row.distanceMi <= 1.000_001).length,
    listedPropertiesWithin5Mi: all.length,
    nearestDistanceMi: all[0]?.distanceMi ?? null,
    listedProperties,
    queriedFeatureCount: (points.features ?? []).length + (polygons.features ?? []).length,
    truncated,
    sourceVintage: snapshotVintage
      ? `NPS unrestricted National Register spatial dataset (local snapshot, vintage ${snapshotVintage})`
      : 'NPS unrestricted National Register spatial dataset; service metadata identifies a September 2020 minor update',
    screeningOnly: true,
    preciseLocationsWithheld: true,
  };
  return {
    data,
    sources: [{
      field: 'culturalResources.publicRegistryScreen',
      source: 'National Park Service National Register of Historic Places unrestricted public spatial dataset',
      url: 'https://www.nps.gov/subjects/nationalregister/data-downloads.htm',
      retrievedAt: new Date().toISOString(),
    }],
  };
}

/**
 * Cache contract version. `withCache` reads Firestore BEFORE provider.fetch,
 * and the cache key encodes only LOCATION — so a change in what produces the
 * answer is invisible to it and stale entries shadow refdata indefinitely.
 *
 * Redhead's SED v34 is the proof: `wetlandAcres: 65.7`, source "... (cached)",
 * served from an entry written before the refdata swap, while live refdata at
 * the same coordinates answers from the 0.1-degree snapshot. hifldGrid needed
 * exactly this bump (v2-refdata-snapshot) and got one; the other seven
 * refdata-backed providers did not, which made the whole migration invisible
 * to every existing site until TTL expiry.
 *
 * Bump this string whenever the DATA SOURCE or its shape changes.
 */
const NPS_NATIONAL_REGISTER_CACHE_CONTRACT_VERSION = 'v2-refdata-snapshot';

export const npsNationalRegisterProvider: DataProvider<CulturalResourcesScreenData> = {
  id: 'nps-nrhp',
  name: 'NPS National Register of Historic Places',
  cacheTtlMs: CACHE_TTL.STANDARD,
  requiresKey: false,

  cacheKey: ({ lat, lng }) => `${NPS_NATIONAL_REGISTER_CACHE_CONTRACT_VERSION}_${gridCellKey(lat, lng)}`,
  fetch: (lat, lng, _apiKey, geo) => fetchNpsNationalRegisterScreen(lat, lng, geo),
};
